/**
 * @architecture-header 1
 * @layer host
 * @domain browser
 * @responsibility Create browser-specific API, external-navigation, and demo-mode adapters.
 * @state-owner immutable host configuration
 * @allowed-imports @vispo/api-client; @vispo/workspace-web environment type
 * @forbidden domain modules; router tree; query wrappers; design system
 * @public-api web main entry only
 * @side-effects browser navigation and authenticated HTTP transport
 * @tests browser-host.test.ts
 * @security-boundary browser host to external network
 * @data-classification access token in memory only
 * @trust-model environment values and URLs are untrusted and validated before use
 */

import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { createVispoApiClient } from '@vispo/api-client';
import { seatStatusSchema } from '@vispo/contracts';
import type {
  WorkspaceAuthAdapter,
  WorkspaceEnvironment,
  WorkspacePresenceRecord,
  WorkspaceRealtimeAdapter,
  WorkspaceSession,
} from '@vispo/workspace-web';
import { clearBrowserTransientState } from './browser-storage';
import type { BrowserRuntimeConfig } from './runtime-config';

function projectSession(session: Session | null): WorkspaceSession | null {
  if (!session) return null;
  const displayName =
    typeof session.user.user_metadata.full_name === 'string'
      ? session.user.user_metadata.full_name
      : typeof session.user.user_metadata.name === 'string'
        ? session.user.user_metadata.name
        : null;
  return {
    userId: session.user.id,
    email: session.user.email ?? '',
    displayName,
  };
}

function createSupabaseAuthAdapter(client: SupabaseClient): WorkspaceAuthAdapter {
  return {
    async getSession() {
      const { data, error } = await client.auth.getSession();
      if (error) throw error;
      return projectSession(data.session);
    },
    subscribe(listener) {
      const {
        data: { subscription },
      } = client.auth.onAuthStateChange((_event, session) => listener(projectSession(session)));
      return () => subscription.unsubscribe();
    },
    async signInWithPassword(credentials) {
      const { error } = await client.auth.signInWithPassword(credentials);
      if (error) throw error;
    },
    async signInWithGoogle() {
      const { error } = await client.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.href },
      });
      if (error) throw error;
    },
    async signOut() {
      const { error } = await client.auth.signOut();
      if (error) throw error;
      clearBrowserTransientState();
    },
  };
}

function createDemoAuthAdapter(): WorkspaceAuthAdapter {
  let session: WorkspaceSession | null = {
    userId: 'demo-user',
    email: 'demo@vispo.local',
    displayName: 'Demo User',
  };
  const listeners = new Set<(nextSession: WorkspaceSession | null) => void>();
  const publish = () => {
    for (const listener of listeners) listener(session);
  };
  return {
    getSession: () => Promise.resolve(session),
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async signInWithPassword({ email }) {
      session = { userId: 'demo-user', email, displayName: 'Demo User' };
      publish();
    },
    async signInWithGoogle() {
      session = {
        userId: 'demo-user',
        email: 'demo@vispo.local',
        displayName: 'Demo User',
      };
      publish();
    },
    async signOut() {
      session = null;
      clearBrowserTransientState();
      publish();
    },
  };
}

function projectPresence(value: unknown): WorkspacePresenceRecord | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (
    typeof row.userId !== 'string' ||
    typeof row.studentName !== 'string' ||
    !seatStatusSchema.safeParse(row.status).success ||
    typeof row.lastActiveAt !== 'string'
  ) {
    return null;
  }
  const confusionAlerts = Array.isArray(row.confusionAlerts)
    ? row.confusionAlerts.flatMap(
        (alert): NonNullable<WorkspacePresenceRecord['confusionAlerts']> => {
          if (!alert || typeof alert !== 'object') return [];
          const item = alert as Record<string, unknown>;
          if (
            !['stuck', 'modified', 'manual'].includes(String(item.type)) ||
            typeof item.questionIndex !== 'number' ||
            typeof item.timestamp !== 'string'
          ) {
            return [];
          }
          return [
            {
              type: item.type as 'stuck' | 'modified' | 'manual',
              questionIndex: item.questionIndex,
              timestamp: item.timestamp,
            },
          ];
        },
      )
    : undefined;
  return {
    userId: row.userId,
    studentName: row.studentName,
    status: row.status as WorkspacePresenceRecord['status'],
    lastActiveAt: row.lastActiveAt,
    ...(typeof row.examId === 'string' ? { examId: row.examId } : {}),
    ...(typeof row.examTitle === 'string' ? { examTitle: row.examTitle } : {}),
    ...(typeof row.phase === 'string' ? { phase: row.phase } : {}),
    ...(typeof row.currentQuestionIndex === 'number'
      ? { currentQuestionIndex: row.currentQuestionIndex }
      : {}),
    ...(typeof row.totalQuestions === 'number' ? { totalQuestions: row.totalQuestions } : {}),
    ...(confusionAlerts ? { confusionAlerts } : {}),
  };
}

function createRealtimeAdapter(client: SupabaseClient | null): WorkspaceRealtimeAdapter {
  return {
    async subscribeClassPresence(classId, listener) {
      if (!client) {
        listener({ connected: false, records: [] });
        return () => undefined;
      }
      const { data, error } = await client.auth.getSession();
      if (error) throw error;
      if (data.session?.access_token) await client.realtime.setAuth(data.session.access_token);
      const channel = client.channel(`class_presence:${classId}`, {
        config: { presence: { key: `listener_${data.session?.user.id ?? crypto.randomUUID()}` } },
      });
      const publish = (connected: boolean) => {
        const records = Object.values(channel.presenceState())
          .flat()
          .flatMap((entry) => {
            const record = projectPresence(entry);
            return record ? [record] : [];
          });
        listener({ connected, records });
      };
      channel
        .on('presence', { event: 'sync' }, () => publish(true))
        .subscribe((status) => publish(status === 'SUBSCRIBED'));
      return () => {
        void channel.untrack();
        void client.removeChannel(channel);
      };
    },
    async trackClassPresence(classId, record) {
      if (!client) return () => undefined;
      const { data, error } = await client.auth.getSession();
      if (error) throw error;
      if (data.session?.access_token) await client.realtime.setAuth(data.session.access_token);
      const channel = client.channel(`class_presence:${classId}`, {
        config: { presence: { key: `student_${record.userId}` } },
      });
      channel.subscribe((status) => {
        if (status === 'SUBSCRIBED') void channel.track(record);
      });
      return () => {
        void channel.untrack();
        void client.removeChannel(channel);
      };
    },
  };
}

export function createBrowserEnvironment(config: BrowserRuntimeConfig): WorkspaceEnvironment {
  const configuredBaseUrl = config.apiUrl.trim();
  const configuredSupabaseUrl = config.supabaseUrl.trim();
  const configuredSupabasePublishableKey = config.supabasePublishableKey.trim();
  const demoMode = config.demoMode;
  const supabase = demoMode
    ? null
    : createClient(configuredSupabaseUrl, configuredSupabasePublishableKey, {
        auth: {
          autoRefreshToken: true,
          detectSessionInUrl: true,
          persistSession: true,
        },
      });
  const auth = supabase ? createSupabaseAuthAdapter(supabase) : createDemoAuthAdapter();

  return {
    apiClient: createVispoApiClient({
      baseUrl: configuredBaseUrl || 'http://127.0.0.1:8080',
      getAccessToken: async () => {
        if (!supabase) return null;
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        return data.session?.access_token ?? null;
      },
      refreshAccessToken: async () => {
        if (!supabase) return null;
        const { data, error } = await supabase.auth.refreshSession();
        if (error) return null;
        return data.session?.access_token ?? null;
      },
    }),
    auth,
    realtime: createRealtimeAdapter(supabase),
    platform: 'browser',
    demoMode,
    async openExternal(url) {
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:') throw new Error('Only HTTPS links can be opened');
      window.open(parsed.href, '_blank', 'noopener,noreferrer');
    },
  };
}
